import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Mail, MousePointer, TrendingUp, Users, Calendar, ExternalLink, User, Clock, CheckCircle, XCircle, Send, Eye, BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format, subDays, startOfDay, isValid, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

// Helper function to safely format dates
const safeFormatDate = (dateString: string | null | undefined, formatStr: string): string => {
  if (!dateString) return "-";
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    if (!isValid(date)) return "-";
    return format(date, formatStr, { locale: fr });
  } catch {
    return "-";
  }
};

// Helper to safely compare dates for sorting
const safeGetTime = (dateString: string | null | undefined): number => {
  if (!dateString) return 0;
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    if (!isValid(date)) return 0;
    return date.getTime();
  } catch {
    return 0;
  }
};

interface EmailClick {
  id: string;
  email: string;
  email_type: string;
  link_name: string;
  destination_url: string;
  clicked_at: string;
  user_agent: string | null;
}

interface EmailOpen {
  id: string;
  email: string;
  email_type: string;
  campaign_id: string | null;
  opened_at: string;
}

interface MassEmailCampaign {
  id: string;
  subject: string;
  message: string;
  recipients_count: number;
  sent_at: string;
  recipients: MassEmailRecipient[];
  clicks: EmailClick[];
  opens: EmailOpen[];
}

interface MassEmailRecipient {
  id: string;
  campaign_id: string;
  email: string;
  sent_at: string;
  opened_at: string | null;
}

interface EmailStats {
  type: string;
  clicks: number;
  uniqueUsers: number;
}

interface ClientEmailHistory {
  email: string;
  type: string;
  status: string;
  created_at: string;
  credentials_sent_at: string | null;
  followup_sent_at: string | null;
  converted_at: string | null;
  codes_exhausted: boolean;
  mass_emails_sent: Array<{ date: string; subject: string }> | Record<string, string> | null;
  clicks: EmailClick[];
}

const AdminEmailAnalyticsSection = () => {
  const [clicks, setClicks] = useState<EmailClick[]>([]);
  const [opens, setOpens] = useState<EmailOpen[]>([]);
  const [campaigns, setCampaigns] = useState<MassEmailCampaign[]>([]);
  const [stats, setStats] = useState<EmailStats[]>([]);
  const [clientsHistory, setClientsHistory] = useState<ClientEmailHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30");
  const [searchEmail, setSearchEmail] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([fetchEmailClicks(), fetchClientsHistory(), fetchCampaigns(), fetchEmailOpens()]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmailOpens = async () => {
    try {
      const startDate = startOfDay(subDays(new Date(), parseInt(dateRange)));
      
      const { data, error } = await supabase
        .from("email_opens")
        .select("*")
        .gte("opened_at", startDate.toISOString())
        .order("opened_at", { ascending: false });

      if (error) throw error;
      setOpens(data || []);
    } catch (error) {
      console.error("Error fetching email opens:", error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const startDate = startOfDay(subDays(new Date(), parseInt(dateRange)));
      
      // Fetch campaigns
      const { data: campaignsData, error: campaignsError } = await supabase
        .from("mass_email_campaigns")
        .select("*")
        .gte("sent_at", startDate.toISOString())
        .order("sent_at", { ascending: false });

      if (campaignsError) throw campaignsError;

      // Fetch recipients for all campaigns
      const campaignIds = (campaignsData || []).map(c => c.id);
      
      let recipients: MassEmailRecipient[] = [];
      if (campaignIds.length > 0) {
        const { data: recipientsData } = await supabase
          .from("mass_email_recipients")
          .select("*")
          .in("campaign_id", campaignIds);
        recipients = recipientsData || [];
      }

      // Fetch clicks for mass_email type
      const { data: massClicks } = await supabase
        .from("email_clicks")
        .select("*")
        .eq("email_type", "mass_email")
        .gte("clicked_at", startDate.toISOString());

      // Fetch opens for campaigns
      let campaignOpens: EmailOpen[] = [];
      if (campaignIds.length > 0) {
        const { data: opensData } = await supabase
          .from("email_opens")
          .select("*")
          .in("campaign_id", campaignIds);
        campaignOpens = opensData || [];
      }

      // Combine data with deduplication
      const enrichedCampaigns: MassEmailCampaign[] = (campaignsData || []).map(campaign => {
        const campaignRecipients = recipients.filter(r => r.campaign_id === campaign.id);
        const campaignRecipientEmails = new Set(campaignRecipients.map(r => r.email.toLowerCase()));
        
        // Deduplicate opens by email (keep first per email)
        const rawOpens = campaignOpens.filter(o => o.campaign_id === campaign.id);
        const seenOpenEmails = new Set<string>();
        const uniqueOpens = rawOpens.filter(o => {
          const key = o.email.toLowerCase();
          if (seenOpenEmails.has(key)) return false;
          seenOpenEmails.add(key);
          return true;
        });

        // Deduplicate clicks by email - prefer campaign_id match, fallback to email match
        const campaignClicks = (massClicks || []).filter(c => {
          // If click has campaign_id, use it for precise attribution
          if ((c as any).campaign_id) return (c as any).campaign_id === campaign.id;
          // Fallback: match by recipient email (legacy clicks without campaign_id)
          return campaignRecipientEmails.has(c.email.toLowerCase());
        });
        const seenClickEmails = new Set<string>();
        const uniqueClicks = campaignClicks.filter(c => {
          const key = c.email.toLowerCase();
          if (seenClickEmails.has(key)) return false;
          seenClickEmails.add(key);
          return true;
        });

        return {
          ...campaign,
          recipients: campaignRecipients,
          clicks: uniqueClicks,
          opens: uniqueOpens,
        };
      });

      setCampaigns(enrichedCampaigns);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const fetchEmailClicks = async () => {
    try {
      const startDate = startOfDay(subDays(new Date(), parseInt(dateRange)));
      
      const { data, error } = await supabase
        .from("email_clicks")
        .select("*")
        .gte("clicked_at", startDate.toISOString())
        .order("clicked_at", { ascending: false });

      if (error) throw error;

      setClicks(data || []);
      
      // Calculate stats by email type
      const statsMap = new Map<string, { clicks: number; uniqueEmails: Set<string> }>();
      
      (data || []).forEach((click) => {
        const existing = statsMap.get(click.email_type) || { clicks: 0, uniqueEmails: new Set() };
        existing.clicks++;
        existing.uniqueEmails.add(click.email);
        statsMap.set(click.email_type, existing);
      });

      const calculatedStats: EmailStats[] = Array.from(statsMap.entries()).map(([type, data]) => ({
        type,
        clicks: data.clicks,
        uniqueUsers: data.uniqueEmails.size,
      }));

      setStats(calculatedStats);
    } catch (error) {
      console.error("Error fetching email clicks:", error);
    }
  };

  const fetchClientsHistory = async () => {
    try {
      // Fetch all requests with test type
      const { data: requests, error: requestsError } = await supabase
        .from("requests")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      if (requestsError) throw requestsError;

      // Fetch all clicks
      const { data: allClicks, error: clicksError } = await supabase
        .from("email_clicks")
        .select("*")
        .order("clicked_at", { ascending: false });

      if (clicksError) throw clicksError;

      // Group clicks by email
      const clicksByEmail = new Map<string, EmailClick[]>();
      (allClicks || []).forEach((click) => {
        const existing = clicksByEmail.get(click.email.toLowerCase()) || [];
        existing.push(click);
        clicksByEmail.set(click.email.toLowerCase(), existing);
      });

      // Create client history
      const history: ClientEmailHistory[] = (requests || []).map((request) => ({
        email: request.email,
        type: request.type,
        status: request.status,
        created_at: request.created_at,
        credentials_sent_at: request.credentials_sent_at,
        followup_sent_at: request.followup_sent_at,
        converted_at: request.converted_at,
        codes_exhausted: request.codes_exhausted,
        mass_emails_sent: request.mass_emails_sent as ClientEmailHistory['mass_emails_sent'],
        clicks: clicksByEmail.get(request.email.toLowerCase()) || [],
      }));

      setClientsHistory(history);
    } catch (error) {
      console.error("Error fetching clients history:", error);
    }
  };

  const getEmailTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      credentials: "Identifiants envoyés",
      confirmation: "Email confirmation",
      new_codes: "Nouveaux codes dispo",
      test_expired: "Test expiré (24h)",
      reminder_24h: "Rappel 24h",
      special_offer: "Offre spéciale (-20%)",
      mass_email: "Email de masse",
      followup: "Relance",
      matchday_promo: "Promo Jour de Match",
    };
    return labels[type] || type;
  };

  const getEmailTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      credentials: "bg-green-500/20 text-green-400 border-green-500/30",
      confirmation: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      new_codes: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      test_expired: "bg-red-500/20 text-red-400 border-red-500/30",
      reminder_24h: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      special_offer: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      mass_email: "bg-pink-500/20 text-pink-400 border-pink-500/30",
      followup: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      matchday_promo: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    };
    return colors[type] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      test_24h: "Test 24h",
      "3months": "3 mois",
      "6months": "6 mois",
      "12months": "12 mois",
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    if (status === "credentials_sent") {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Identifiants envoyés</Badge>;
    }
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Nouveau</Badge>;
  };

  const totalClicks = clicks.length;
  const uniqueUsers = new Set(clicks.map(c => c.email)).size;
  const todayClicks = clicks.filter(c => 
    new Date(c.clicked_at).toDateString() === new Date().toDateString()
  ).length;

  // Filter clients by search
  const filteredClients = clientsHistory.filter(client =>
    client.email.toLowerCase().includes(searchEmail.toLowerCase())
  );

  // Calculate funnel metrics
  const totalTests = clientsHistory.filter(c => c.type === "test_24h").length;
  const credentialsSent = clientsHistory.filter(c => c.credentials_sent_at).length;
  const followupsSent = clientsHistory.filter(c => c.followup_sent_at).length;
  const conversions = clientsHistory.filter(c => c.converted_at).length;
  const conversionRate = totalTests > 0 ? ((conversions / totalTests) * 100).toFixed(1) : "0";

  // Matchday promo metrics
  const matchdayPromoSent = clientsHistory.filter(c => c.type === "test_24h" && c.codes_exhausted === true).length;
  const matchdayOpens = opens.filter(o => o.email_type === "matchday_promo").length;
  const matchdayUniqueOpens = new Set(opens.filter(o => o.email_type === "matchday_promo").map(o => o.email)).size;
  const matchdayClicks = clicks.filter(c => c.email_type === "matchday_promo").length;
  const matchdayUniqueClicks = new Set(clicks.filter(c => c.email_type === "matchday_promo").map(c => c.email)).size;
  const matchdayExhaustedEmails = new Set(clientsHistory.filter(c => c.type === "test_24h" && c.codes_exhausted === true).map(c => c.email.toLowerCase()));
  const matchdayConversions = clientsHistory.filter(c => c.converted_at && matchdayExhaustedEmails.has(c.email.toLowerCase())).length;
  const matchdayConversionRate = matchdayPromoSent > 0 ? ((matchdayConversions / matchdayPromoSent) * 100).toFixed(1) : "0.0";

  // Comprehensive per-type stats
  const computePerTypeStats = () => {
    const allTypes = [
      { key: "confirmation", label: "Confirmation", sent: clientsHistory.length },
      { key: "credentials", label: "Identifiants", sent: credentialsSent },
      { key: "test_expired", label: "Test expiré (24h)", sent: clientsHistory.filter(c => c.followup_sent_at).length },
      { key: "special_offer", label: "Offre spéciale (-20%)", sent: clientsHistory.filter(c => {
        const massEmails = c.mass_emails_sent;
        return Array.isArray(massEmails) && massEmails.some((m: any) => m.subject?.includes("exclusive"));
      }).length || clicks.filter(c => c.email_type === "special_offer").length },
      { key: "matchday_promo", label: "Victime de notre succès", sent: matchdayPromoSent },
      { key: "mass_email", label: "Email de masse", sent: campaigns.reduce((acc, c) => acc + c.recipients_count, 0) },
      { key: "reminder_24h", label: "Rappel 24h", sent: opens.filter(o => o.email_type === "reminder_24h").length + clicks.filter(c => c.email_type === "reminder_24h").length > 0 ? Math.max(opens.filter(o => o.email_type === "reminder_24h").length, clicks.filter(c => c.email_type === "reminder_24h").length) : 0 },
    ];

    return allTypes.map(t => {
      const typeOpens = opens.filter(o => o.email_type === t.key);
      const typeClicks = clicks.filter(c => c.email_type === t.key);
      const uniqueOpens = new Set(typeOpens.map(o => o.email)).size;
      const uniqueClicks = new Set(typeClicks.map(c => c.email)).size;
      const openRate = t.sent > 0 ? ((uniqueOpens / t.sent) * 100).toFixed(1) : "-";
      const clickRate = t.sent > 0 ? ((uniqueClicks / t.sent) * 100).toFixed(1) : "-";
      return { ...t, opens: uniqueOpens, clicks: uniqueClicks, openRate, clickRate };
    }).filter(t => t.sent > 0 || t.opens > 0 || t.clicks > 0);
  };

  const perTypeStats = computePerTypeStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with date filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-white">📧 Analytics Emails</h2>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[180px] bg-white/5 border-white/10">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Aujourd'hui</SelectItem>
            <SelectItem value="7">7 derniers jours</SelectItem>
            <SelectItem value="30">30 derniers jours</SelectItem>
            <SelectItem value="90">90 derniers jours</SelectItem>
            <SelectItem value="365">Cette année</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border-white/10">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="campaigns">Campagnes</TabsTrigger>
          <TabsTrigger value="clients">Suivi clients</TabsTrigger>
          <TabsTrigger value="clicks">Détail clics</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Funnel Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex flex-col">
                  <p className="text-xs text-gray-400">Tests demandés</p>
                  <p className="text-2xl font-bold text-white">{totalTests}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex flex-col">
                  <p className="text-xs text-gray-400">Identifiants envoyés</p>
                  <p className="text-2xl font-bold text-white">{credentialsSent}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
              <CardContent className="p-4">
                <div className="flex flex-col">
                  <p className="text-xs text-gray-400">Relances envoyées</p>
                  <p className="text-2xl font-bold text-white">{followupsSent}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex flex-col">
                  <p className="text-xs text-gray-400">Clics emails</p>
                  <p className="text-2xl font-bold text-white">{totalClicks}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
              <CardContent className="p-4">
                <div className="flex flex-col">
                  <p className="text-xs text-gray-400">Taux conversion</p>
                  <p className="text-2xl font-bold text-white">{conversionRate}%</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Matchday Promo Stats */}
          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-600/5 border-amber-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                🔥 Email "Victime de notre succès" (Mode Pause)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-3 bg-amber-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{matchdayPromoSent}</p>
                  <p className="text-xs text-gray-400">Envoyés</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">{matchdayUniqueOpens}</p>
                  <p className="text-xs text-gray-400">Ouvertures</p>
                </div>
                <div className="p-3 bg-purple-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-400">{matchdayUniqueClicks}</p>
                  <p className="text-xs text-gray-400">Clics</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-400">{matchdayConversions}</p>
                  <p className="text-xs text-gray-400">Conversions</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    {matchdayConversionRate}%
                  </p>
                  <p className="text-xs text-gray-400">Taux conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>


          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total Clics</p>
                    <p className="text-3xl font-bold text-white">{totalClicks}</p>
                  </div>
                  <MousePointer className="h-10 w-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Utilisateurs uniques</p>
                    <p className="text-3xl font-bold text-white">{uniqueUsers}</p>
                  </div>
                  <Users className="h-10 w-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Clics aujourd'hui</p>
                    <p className="text-3xl font-bold text-white">{todayClicks}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Types d'emails</p>
                    <p className="text-3xl font-bold text-white">{stats.length}</p>
                  </div>
                  <Mail className="h-10 w-10 text-orange-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats by Email Type - Comprehensive */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Performance par type d'email
              </CardTitle>
            </CardHeader>
            <CardContent>
              {perTypeStats.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Aucune donnée pour cette période</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-gray-400">Type</TableHead>
                        <TableHead className="text-gray-400 text-center">Envoyés</TableHead>
                        <TableHead className="text-gray-400 text-center">Ouvertures</TableHead>
                        <TableHead className="text-gray-400 text-center">Taux ouverture</TableHead>
                        <TableHead className="text-gray-400 text-center">Clics</TableHead>
                        <TableHead className="text-gray-400 text-center">Taux clic</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {perTypeStats.map((stat) => (
                        <TableRow key={stat.key} className="border-white/10">
                          <TableCell>
                            <Badge className={getEmailTypeBadgeColor(stat.key)}>
                              {stat.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center text-white font-bold">{stat.sent}</TableCell>
                          <TableCell className="text-center text-white">{stat.opens}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-medium ${stat.openRate !== "-" && parseFloat(stat.openRate) > 20 ? "text-green-400" : stat.openRate !== "-" && parseFloat(stat.openRate) > 10 ? "text-yellow-400" : "text-gray-400"}`}>
                              {stat.openRate === "-" ? "-" : `${stat.openRate}%`}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-white">{stat.clicks}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-medium ${stat.clickRate !== "-" && parseFloat(stat.clickRate) > 5 ? "text-green-400" : stat.clickRate !== "-" && parseFloat(stat.clickRate) > 2 ? "text-yellow-400" : "text-gray-400"}`}>
                              {stat.clickRate === "-" ? "-" : `${stat.clickRate}%`}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          {/* Campaign Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Total campagnes</p>
                    <p className="text-3xl font-bold text-white">{campaigns.length}</p>
                  </div>
                  <Send className="h-10 w-10 text-pink-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Emails envoyés</p>
                    <p className="text-3xl font-bold text-white">
                      {campaigns.reduce((acc, c) => acc + c.recipients_count, 0)}
                    </p>
                  </div>
                  <Mail className="h-10 w-10 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Taux d'ouverture</p>
                    <p className="text-3xl font-bold text-white">
                      {campaigns.length > 0 
                        ? ((campaigns.reduce((acc, c) => acc + c.opens.length, 0) / 
                            campaigns.reduce((acc, c) => acc + c.recipients_count, 0)) * 100 || 0).toFixed(1)
                        : "0"}%
                    </p>
                  </div>
                  <Eye className="h-10 w-10 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400">Taux de clic</p>
                    <p className="text-3xl font-bold text-white">
                      {campaigns.length > 0 
                        ? ((campaigns.reduce((acc, c) => acc + c.clicks.length, 0) / 
                            campaigns.reduce((acc, c) => acc + c.recipients_count, 0)) * 100 || 0).toFixed(1)
                        : "0"}%
                    </p>
                  </div>
                  <MousePointer className="h-10 w-10 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Campaigns List */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Historique des campagnes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Aucune campagne pour cette période</p>
              ) : (
                <div className="space-y-4">
                  {campaigns.map((campaign) => {
                    const openRate = campaign.recipients_count > 0 
                      ? ((campaign.opens.length / campaign.recipients_count) * 100).toFixed(1)
                      : "0";
                    const clickRate = campaign.recipients_count > 0 
                      ? ((campaign.clicks.length / campaign.recipients_count) * 100).toFixed(1)
                      : "0";
                    // opens and clicks are already deduplicated
                    const isExpanded = expandedCampaign === campaign.id;

                    return (
                      <Collapsible
                        key={campaign.id}
                        open={isExpanded}
                        onOpenChange={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
                      >
                        <div className="border border-white/10 rounded-lg overflow-hidden">
                          <CollapsibleTrigger className="w-full">
                            <div className="p-4 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="flex flex-col items-start">
                                    <span className="text-white font-medium text-left">
                                      {campaign.subject}
                                    </span>
                                    <span className="text-gray-400 text-sm">
                                      {safeFormatDate(campaign.sent_at, "dd MMM yyyy 'à' HH:mm")}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-6">
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1">
                                      <Mail className="h-4 w-4 text-blue-400" />
                                      <span className="text-white">{campaign.recipients_count}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Eye className="h-4 w-4 text-green-400" />
                                      <span className="text-white">{campaign.opens.length}</span>
                                      <span className="text-gray-400">({openRate}%)</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <MousePointer className="h-4 w-4 text-purple-400" />
                                      <span className="text-white">{campaign.clicks.length}</span>
                                      <span className="text-gray-400">({clickRate}%)</span>
                                    </div>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="p-4 border-t border-white/10 bg-white/[0.02]">
                              {/* Message Preview */}
                              <div className="mb-4">
                                <h4 className="text-sm font-medium text-gray-400 mb-2">Contenu du message</h4>
                                <div className="p-3 bg-white/5 rounded-lg text-gray-300 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                                  {campaign.message}
                                </div>
                              </div>

                              {/* Stats Grid */}
                              <div className="grid grid-cols-3 gap-4 mb-4">
                                <div className="p-3 bg-blue-500/10 rounded-lg text-center">
                                  <p className="text-2xl font-bold text-blue-400">{campaign.recipients_count}</p>
                                  <p className="text-xs text-gray-400">Envoyés</p>
                                </div>
                                <div className="p-3 bg-green-500/10 rounded-lg text-center">
                                  <p className="text-2xl font-bold text-green-400">{campaign.opens.length}</p>
                                  <p className="text-xs text-gray-400">Ouvertures ({openRate}%)</p>
                                </div>
                                <div className="p-3 bg-purple-500/10 rounded-lg text-center">
                                  <p className="text-2xl font-bold text-purple-400">{campaign.clicks.length}</p>
                                  <p className="text-xs text-gray-400">Clics ({clickRate}%)</p>
                                </div>
                              </div>

                              {/* Recipients who opened */}
                              {campaign.opens.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-gray-400 mb-2">
                                    Ouvertures ({campaign.opens.length})
                                  </h4>
                                  <div className="max-h-32 overflow-y-auto">
                                    <div className="flex flex-wrap gap-2">
                                      {campaign.opens.slice(0, 20).map((open, idx) => (
                                        <Badge 
                                          key={idx} 
                                          className="bg-green-500/20 text-green-400 border-green-500/30"
                                        >
                                          {open.email}
                                        </Badge>
                                      ))}
                                      {campaign.opens.length > 20 && (
                                        <Badge className="bg-gray-500/20 text-gray-400">
                                          +{campaign.opens.length - 20} autres
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Recipients who clicked */}
                              {campaign.clicks.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-gray-400 mb-2">
                                    Clics ({campaign.clicks.length})
                                  </h4>
                                  <div className="max-h-32 overflow-y-auto">
                                    <div className="flex flex-wrap gap-2">
                                      {campaign.clicks.slice(0, 20).map((click, idx) => (
                                        <Badge 
                                          key={idx} 
                                          className="bg-purple-500/20 text-purple-400 border-purple-500/30"
                                        >
                                          {click.email}
                                        </Badge>
                                      ))}
                                      {campaign.clicks.length > 20 && (
                                        <Badge className="bg-gray-500/20 text-gray-400">
                                          +{campaign.clicks.length - 20} autres
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-6">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Rechercher par email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <p className="text-gray-400 text-sm">{filteredClients.length} client(s)</p>
          </div>

          {/* Clients Table */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5" />
                Suivi détaillé par client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Type</TableHead>
                      <TableHead className="text-gray-400">Statut</TableHead>
                      <TableHead className="text-gray-400">Inscrit le</TableHead>
                      <TableHead className="text-gray-400">Identifiants</TableHead>
                      <TableHead className="text-gray-400">Relance</TableHead>
                      <TableHead className="text-gray-400">Clics</TableHead>
                      <TableHead className="text-gray-400">Converti</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.slice(0, 100).map((client, index) => (
                      <TableRow key={`${client.email}-${index}`} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white font-medium">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-500" />
                            {client.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-white/20 text-gray-300">
                            {getTypeLabel(client.type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(client.status)}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {safeFormatDate(client.created_at, "dd/MM/yy HH:mm")}
                        </TableCell>
                        <TableCell>
                          {client.credentials_sent_at ? (
                            <div className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-xs">
                                {safeFormatDate(client.credentials_sent_at, "dd/MM HH:mm")}
                              </span>
                            </div>
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          {client.followup_sent_at ? (
                            <div className="flex items-center gap-1 text-orange-400">
                              <Send className="h-4 w-4" />
                              <span className="text-xs">
                                {safeFormatDate(client.followup_sent_at, "dd/MM HH:mm")}
                              </span>
                            </div>
                          ) : (
                            <Clock className="h-4 w-4 text-gray-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          {client.clicks.length > 0 ? (
                            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                              {client.clicks.length} clic(s)
                            </Badge>
                          ) : (
                            <span className="text-gray-500 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {client.converted_at ? (
                            <div className="flex items-center gap-1 text-emerald-400">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-xs">
                                {safeFormatDate(client.converted_at, "dd/MM")}
                              </span>
                            </div>
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-500" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {filteredClients.length > 100 && (
                <p className="text-gray-400 text-sm text-center mt-4">
                  Affichage limité aux 100 premiers résultats
                </p>
              )}
            </CardContent>
          </Card>

          {/* Mass Emails Sent Summary */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Send className="h-5 w-5" />
                Derniers emails de masse envoyés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Sujet</TableHead>
                      <TableHead className="text-gray-400">Date d'envoi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients
                      .filter(c => c.mass_emails_sent && (Array.isArray(c.mass_emails_sent) ? c.mass_emails_sent.length > 0 : Object.keys(c.mass_emails_sent).length > 0))
                      .slice(0, 50)
                      .flatMap(client => {
                        if (Array.isArray(client.mass_emails_sent)) {
                          return client.mass_emails_sent.map((email, idx) => ({
                            clientEmail: client.email,
                            subject: email.subject,
                            date: email.date,
                            key: `${client.email}-${idx}`
                          }));
                        } else if (client.mass_emails_sent) {
                          return Object.entries(client.mass_emails_sent).map(([key, date]) => ({
                            clientEmail: client.email,
                            subject: key === 'special_offer' ? 'Offre spéciale -20%' : key,
                            date: date as string,
                            key: `${client.email}-${key}`
                          }));
                        }
                        return [];
                      })
                      .filter(item => item.date && safeGetTime(item.date) > 0)
                      .sort((a, b) => safeGetTime(b.date) - safeGetTime(a.date))
                      .slice(0, 30)
                      .map((item) => (
                        <TableRow key={item.key} className="border-white/10">
                          <TableCell className="text-white">{item.clientEmail}</TableCell>
                          <TableCell className="text-gray-300">{item.subject}</TableCell>
                          <TableCell className="text-gray-400">
                            {safeFormatDate(item.date, "dd MMM yyyy HH:mm")}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clicks Tab */}
        <TabsContent value="clicks" className="space-y-6">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MousePointer className="h-5 w-5" />
                Détail des clics ({clicks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clicks.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Aucun clic enregistré</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-gray-400">Email</TableHead>
                        <TableHead className="text-gray-400">Type d'email</TableHead>
                        <TableHead className="text-gray-400">Lien cliqué</TableHead>
                        <TableHead className="text-gray-400">Destination</TableHead>
                        <TableHead className="text-gray-400">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clicks.slice(0, 100).map((click) => (
                        <TableRow key={click.id} className="border-white/10">
                          <TableCell className="text-white font-medium">
                            {click.email}
                          </TableCell>
                          <TableCell>
                            <Badge className={getEmailTypeBadgeColor(click.email_type)}>
                              {getEmailTypeLabel(click.email_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {click.link_name}
                          </TableCell>
                          <TableCell className="text-gray-400">
                            <div className="flex items-center gap-1 max-w-[200px] truncate">
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{click.destination_url}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {safeFormatDate(click.clicked_at, "dd MMM yyyy HH:mm")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminEmailAnalyticsSection;